import { motion } from 'framer-motion';
import { Loader2, Activity } from 'lucide-react';
import clsx from 'clsx';

const Loading = ({
  variant = 'spinner',
  size = 'md',
  text = '',
  overlay = false,
  fullScreen = false,
  className,
  color = 'primary'
}) => {
  
  // Tamaños
  const sizes = {
    xs: 'w-4 h-4',
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  // Colores
  const colors = {
    primary: 'text-primary-600',
    secondary: 'text-secondary-600',
    success: 'text-success-600',
    warning: 'text-warning-600',
    error: 'text-error-600',
    white: 'text-white',
  };

  // Clases del contenedor
  const containerClasses = clsx(
    'flex flex-col items-center justify-center',
    {
      // Overlay
      'fixed inset-0 bg-black/50 backdrop-blur-sm z-50': overlay || fullScreen,
      'min-h-screen': fullScreen && !overlay,
    },
    className
  );

  // Renderizar diferentes variantes de loading
  const renderVariant = () => {
    switch (variant) {
      case 'spinner':
        return (
          <Loader2 
            className={clsx(sizes[size], colors[color], 'animate-spin')}
          />
        );

      case 'pulse':
        return (
          <motion.div
            className={clsx(
              'rounded-full bg-current',
              sizes[size],
              colors[color]
            )}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.7, 1],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        );

      case 'dots':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                className={clsx(
                  'w-2 h-2 rounded-full bg-current',
                  colors[color]
                )}
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: index * 0.1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );

      case 'bars':
        return (
          <div className="flex space-x-1 items-end">
            {[0, 1, 2, 3].map((index) => (
              <motion.div
                key={index}
                className={clsx(
                  'w-1 bg-current rounded-full',
                  colors[color]
                )}
                animate={{
                  height: [8, 24, 8],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: index * 0.1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );

      case 'circle':
        return (
          <div className={clsx('relative', sizes[size])}>
            <div 
              className={clsx(
                'absolute inset-0 rounded-full border-4 border-current opacity-20',
                colors[color]
              )}
            />
            <motion.div
              className={clsx(
                'absolute inset-0 rounded-full border-4 border-transparent border-t-current',
                colors[color]
              )}
              animate={{ rotate: 360 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>
        );

      case 'heartbeat':
        return (
          <motion.div
            className="flex items-center"
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Activity 
              className={clsx(sizes[size], colors[color])}
            />
          </motion.div>
        );

      case 'football':
        return (
          <motion.div
            className={clsx(
              'rounded-full bg-white border-2 border-black relative',
              sizes[size]
            )}
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            {/* Líneas del balón de fútbol */}
            <div className="absolute inset-0 rounded-full">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-black transform -translate-y-1/2" />
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-black transform -translate-x-1/2" />
              <div className="absolute top-1/4 left-1/4 right-1/4 h-0.5 bg-black transform rotate-45" />
              <div className="absolute top-3/4 left-1/4 right-1/4 h-0.5 bg-black transform -rotate-45" />
            </div>
          </motion.div>
        );

      default:
        return (
          <Loader2 
            className={clsx(sizes[size], colors[color], 'animate-spin')}
          />
        );
    }
  };

  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center">
        {renderVariant()}
        
        {text && (
          <motion.p
            className={clsx(
              'mt-3 text-sm font-medium',
              colors[color],
              {
                'text-white': overlay || fullScreen,
              }
            )}
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {text}
          </motion.p>
        )}
      </div>
    </div>
  );
};

// Componente de esqueleto para cargas de contenido
export const Skeleton = ({ 
  className, 
  width = 'w-full', 
  height = 'h-4',
  rounded = 'rounded',
  animation = true 
}) => {
  return (
    <div
      className={clsx(
        'bg-secondary-200 dark:bg-secondary-700',
        width,
        height,
        rounded,
        {
          'animate-pulse': animation,
        },
        className
      )}
    />
  );
};

// Componente de loading para tarjetas
export const CardSkeleton = ({ lines = 3, showAvatar = false }) => {
  return (
    <div className="p-6 space-y-4 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
      {showAvatar && (
        <div className="flex items-center space-x-3">
          <Skeleton width="w-10" height="h-10" rounded="rounded-full" />
          <div className="space-y-2 flex-1">
            <Skeleton width="w-1/4" height="h-3" />
            <Skeleton width="w-1/6" height="h-2" />
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {Array.from({ length: lines }, (_, index) => (
          <Skeleton 
            key={index}
            width={index === lines - 1 ? 'w-3/4' : 'w-full'}
            height="h-3"
          />
        ))}
      </div>
    </div>
  );
};

// Componente de loading para listas
export const ListSkeleton = ({ items = 5, showAvatar = true }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="flex items-center space-x-3 p-3">
          {showAvatar && (
            <Skeleton width="w-8" height="h-8" rounded="rounded-full" />
          )}
          <div className="space-y-2 flex-1">
            <Skeleton width="w-1/3" height="h-3" />
            <Skeleton width="w-1/2" height="h-2" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default Loading;